import {
  DefectLayerKind,
  DefectMeasurementSlug,
  DefectRulesetSlug,
  DefectSlug,
  DefectSurveyYearSlug,
} from '../contracts';
import { getTenantIdFromStorage } from 'utils/tenant';

const STORAGE_PREFIX = 'sdm:sessionLayers:';
const STORAGE_VERSION = 1 as const;

export interface PersistedPersonalLayer {
  readonly defectSlug: DefectSlug;
  readonly measurementSlug: DefectMeasurementSlug;
  readonly surveyYearSlug: DefectSurveyYearSlug;
  readonly rulesetSlug: DefectRulesetSlug;
  readonly lane: string;
  readonly displayName: string;
  readonly visible: boolean;
  readonly opacity: number;
  readonly lineWidth: number;
  readonly lineStyle: string;
  readonly layerKind?: DefectLayerKind;
  readonly sourceLayerId?: string | null;
  readonly vectorSourceUrl?: string | null;
}

export interface PersistedSessionLayers {
  readonly version: typeof STORAGE_VERSION;
  readonly personalLayers: readonly PersistedPersonalLayer[];
  readonly activatedRecipeIds: readonly string[];
  readonly updatedAt: string;
}

function resolveTenantId(): string {
  return getTenantIdFromStorage() ?? 'default';
}

export function buildSessionLayersStorageKey(tenantId: string = resolveTenantId()): string {
  return `${STORAGE_PREFIX}${tenantId}`;
}

function isPersistedPersonalLayer(value: unknown): value is PersistedPersonalLayer {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const layer = value as Record<string, unknown>;
  return (
    typeof layer.defectSlug === 'string' &&
    typeof layer.measurementSlug === 'string' &&
    typeof layer.surveyYearSlug === 'string' &&
    typeof layer.rulesetSlug === 'string' &&
    typeof layer.lane === 'string' &&
    typeof layer.displayName === 'string' &&
    typeof layer.visible === 'boolean' &&
    typeof layer.opacity === 'number' &&
    typeof layer.lineWidth === 'number' &&
    typeof layer.lineStyle === 'string'
  );
}

function parseSessionLayers(raw: string): PersistedSessionLayers | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (record.version !== STORAGE_VERSION) {
      return null;
    }
    if (!Array.isArray(record.personalLayers) || !Array.isArray(record.activatedRecipeIds)) {
      return null;
    }
    if (!record.personalLayers.every(isPersistedPersonalLayer)) {
      return null;
    }
    if (!record.activatedRecipeIds.every((id) => typeof id === 'string')) {
      return null;
    }
    return {
      version: STORAGE_VERSION,
      personalLayers: record.personalLayers,
      activatedRecipeIds: record.activatedRecipeIds,
      updatedAt:
        typeof record.updatedAt === 'string'
          ? record.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function loadPersistedSessionLayers(): PersistedSessionLayers | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(buildSessionLayersStorageKey());
    if (!raw) {
      return null;
    }
    return parseSessionLayers(raw);
  } catch {
    return null;
  }
}

export function savePersistedSessionLayers(
  session: Omit<PersistedSessionLayers, 'version' | 'updatedAt'>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const payload: PersistedSessionLayers = {
    version: STORAGE_VERSION,
    personalLayers: session.personalLayers,
    activatedRecipeIds: session.activatedRecipeIds,
    updatedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(
    buildSessionLayersStorageKey(),
    JSON.stringify(payload),
  );
}

export function clearPersistedSessionLayers(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(buildSessionLayersStorageKey());
}

/** Clears SDM session layer keys for every tenant (logout / hard reset). */
export function clearAllPersistedSessionLayers(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const keysToRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    window.localStorage.removeItem(key);
  }
}
